import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Globe2,
  Mail,
  MessageSquare,
  Phone,
  UserRound,
  Video
} from 'lucide-react';
import { toast } from 'react-toastify';
import { appointmentsAPI, handleApiError } from '../../utils/api';

const getBrowserTimezones = () => {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  if (typeof Intl.supportedValuesOf === 'function') {
    try {
      const values = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(values) && values.length > 0) {
        return values.includes(detected) ? values : [detected, ...values];
      }
    } catch (error) {
      // ignore and fallback
    }
  }
  return [detected, 'UTC', 'Europe/Paris', 'America/Toronto', 'America/New_York', 'Europe/Madrid'];
};

const BookingPage = () => {
  const { bookingSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recruiterInfo, setRecruiterInfo] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [meetingType, setMeetingType] = useState('online');
  const [forcedMeetingType, setForcedMeetingType] = useState(null);
  const [candidateTimezone, setCandidateTimezone] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [language, setLanguage] = useState('fr');

  const [formData, setFormData] = useState({
    candidateName: '',
    candidateEmail: '',
    candidatePhone: '',
    subject: ''
  });

  const translations = useMemo(() => ({
    fr: {
      pageTitle: 'Planifier un entretien',
      subtitle: 'Choisissez votre créneau et confirmez vos informations en quelques secondes.',
      title: 'Prendre rendez-vous avec',
      recruiterCard: 'Informations recruteur',
      duration: 'Durée',
      minNotice: 'Préavis minimum',
      maxAdvance: 'Réservation max',
      selectDate: 'Sélectionnez une date',
      selectTimezone: 'Votre fuseau horaire',
      timezoneHint: 'Les horaires sont affichés dans votre fuseau.',
      selectTime: 'Créneaux disponibles',
      noSlots: 'Aucun créneau disponible pour cette date.',
      chooseDateFirst: 'Sélectionnez d’abord une date pour afficher les créneaux.',
      meetingType: 'Type de rendez-vous',
      online: 'En ligne',
      physical: 'Physique',
      meetingTypeLocked: 'Le type de rendez-vous est imposé par le lien reçu.',
      yourInfo: 'Vos informations',
      name: 'Nom complet',
      email: 'Email',
      phone: 'Téléphone',
      subject: 'Objet du rendez-vous',
      requiredField: 'Champ requis',
      emailInvalid: 'Email invalide',
      book: 'Confirmer la réservation',
      booking: 'Envoi en cours...',
      successTitle: 'Demande envoyée avec succès',
      successMessage: 'Le recruteur a bien reçu votre demande. Vous recevrez un email de confirmation.',
      errorMessage: 'Une erreur est survenue. Veuillez réessayer.',
      notFound: 'Page de réservation introuvable',
      loading: 'Chargement...',
      summaryTitle: 'Récapitulatif',
      selectedSlot: 'Créneau choisi'
    },
    en: {
      pageTitle: 'Schedule an interview',
      subtitle: 'Pick your slot and confirm your details in a few seconds.',
      title: 'Book an appointment with',
      recruiterCard: 'Recruiter information',
      duration: 'Duration',
      minNotice: 'Minimum notice',
      maxAdvance: 'Maximum booking',
      selectDate: 'Select a date',
      selectTimezone: 'Your time zone',
      timezoneHint: 'Slots are shown in your time zone.',
      selectTime: 'Available slots',
      noSlots: 'No slots available for this date.',
      chooseDateFirst: 'Select a date first to display slots.',
      meetingType: 'Meeting type',
      online: 'Online',
      physical: 'In person',
      meetingTypeLocked: 'Meeting type is locked by this booking link.',
      yourInfo: 'Your information',
      name: 'Full name',
      email: 'Email',
      phone: 'Phone',
      subject: 'Meeting subject',
      requiredField: 'Required field',
      emailInvalid: 'Invalid email',
      book: 'Confirm booking',
      booking: 'Submitting...',
      successTitle: 'Request sent successfully',
      successMessage: 'The recruiter received your request. You will receive a confirmation email.',
      errorMessage: 'An error occurred. Please try again.',
      notFound: 'Booking page not found',
      loading: 'Loading...',
      summaryTitle: 'Summary',
      selectedSlot: 'Selected slot'
    },
    es: {
      pageTitle: 'Programar una entrevista',
      subtitle: 'Elige tu franja y confirma tus datos en pocos segundos.',
      title: 'Reservar una cita con',
      recruiterCard: 'Información del reclutador',
      duration: 'Duración',
      minNotice: 'Aviso mínimo',
      maxAdvance: 'Reserva máxima',
      selectDate: 'Seleccione una fecha',
      selectTimezone: 'Su zona horaria',
      timezoneHint: 'Los horarios se muestran en su zona horaria.',
      selectTime: 'Horarios disponibles',
      noSlots: 'No hay horarios disponibles para esta fecha.',
      chooseDateFirst: 'Seleccione primero una fecha para ver horarios.',
      meetingType: 'Tipo de reunión',
      online: 'En línea',
      physical: 'Presencial',
      meetingTypeLocked: 'El tipo de reunión está bloqueado por este enlace.',
      yourInfo: 'Tus datos',
      name: 'Nombre completo',
      email: 'Correo electrónico',
      phone: 'Teléfono',
      subject: 'Asunto de la reunión',
      requiredField: 'Campo obligatorio',
      emailInvalid: 'Correo inválido',
      book: 'Confirmar reserva',
      booking: 'Enviando...',
      successTitle: 'Solicitud enviada con éxito',
      successMessage: 'El reclutador recibió su solicitud. Recibirá un correo de confirmación.',
      errorMessage: 'Ocurrió un error. Inténtelo de nuevo.',
      notFound: 'Página de reserva no encontrada',
      loading: 'Cargando...',
      summaryTitle: 'Resumen',
      selectedSlot: 'Horario seleccionado'
    }
  }), []);

  const t = useCallback((key, fallback) => translations[language]?.[key] || fallback || key, [language, translations]);

  const timezoneOptions = useMemo(() => getBrowserTimezones(), []);

  useEffect(() => {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    setCandidateTimezone(detected);
  }, []);

  const fetchRecruiterData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await appointmentsAPI.getPublicBooking(bookingSlug);
      const data = response.data || {};

      setRecruiterInfo(data);
      setLanguage(data?.recruiter?.language || 'fr');

      const urlParams = new URLSearchParams(window.location.search);
      const forcedType = urlParams.get('type');
      if (forcedType === 'online' || forcedType === 'physical') {
        setForcedMeetingType(forcedType);
        setMeetingType(forcedType);
      }

      setError(null);
    } catch (requestError) {
      if (requestError.response?.status === 404) {
        setError(t('notFound'));
      } else {
        setError(handleApiError(requestError, t('errorMessage')));
      }
    } finally {
      setLoading(false);
    }
  }, [bookingSlug, t]);

  const fetchSlots = useCallback(async (date) => {
    if (!date) return;
    try {
      setSlotsLoading(true);
      const response = await appointmentsAPI.getPublicSlots(bookingSlug, {
        date,
        candidateTimezone
      });
      const slots = response?.data?.slots || [];
      setAvailableSlots(slots);
      if (!slots.find((slot) => slot.start === selectedSlot?.start)) {
        setSelectedSlot(null);
      }
    } catch (requestError) {
      setAvailableSlots([]);
      setSelectedSlot(null);
      toast.error(handleApiError(requestError, t('errorMessage')));
    } finally {
      setSlotsLoading(false);
    }
  }, [bookingSlug, candidateTimezone, selectedSlot?.start, t]);

  useEffect(() => {
    fetchRecruiterData();
  }, [fetchRecruiterData]);

  useEffect(() => {
    if (selectedDate) {
      fetchSlots(selectedDate);
    } else {
      setAvailableSlots([]);
      setSelectedSlot(null);
    }
  }, [fetchSlots, selectedDate]);

  const validate = () => {
    const errors = {};
    if (!selectedDate) errors.selectedDate = t('requiredField');
    if (!selectedSlot) errors.selectedSlot = t('requiredField');
    if (!formData.candidateName.trim()) errors.candidateName = t('requiredField');
    if (!formData.candidateEmail.trim()) {
      errors.candidateEmail = t('requiredField');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.candidateEmail)) {
      errors.candidateEmail = t('emailInvalid');
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error(t('errorMessage'));
      return;
    }

    setSubmitting(true);
    try {
      await appointmentsAPI.bookPublic(bookingSlug, {
        date: selectedDate,
        time: selectedSlot.start,
        candidate_name: formData.candidateName.trim(),
        candidate_email: formData.candidateEmail.trim(),
        candidate_phone: formData.candidatePhone.trim() || undefined,
        subject: formData.subject.trim() || undefined,
        meeting_type: meetingType,
        candidateTimezone
      });

      setBookingSuccess(true);
      toast.success(t('successTitle'));
    } catch (requestError) {
      toast.error(handleApiError(requestError, t('errorMessage')));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="rounded-2xl border border-slate-200 bg-white px-8 py-6 shadow-sm text-slate-600">
          {t('loading')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-2xl border border-red-200 bg-white p-8 shadow-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-red-200 bg-red-50">
            <AlertTriangle className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">{t('notFound')}</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (bookingSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="w-full max-w-xl rounded-2xl border border-emerald-200 bg-white p-8 shadow-sm text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">{t('successTitle')}</h2>
          <p className="mt-3 text-sm leading-6 text-slate-600">{t('successMessage')}</p>
          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('summaryTitle')}</p>
            <p className="mt-2 text-sm text-slate-700">
              <span className="font-medium">{t('selectedSlot')}:</span>{' '}
              {selectedDate} · {selectedSlot?.start} - {selectedSlot?.end}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const recruiterName = recruiterInfo?.recruiter?.name || '';
  const bookingSettings = recruiterInfo?.settings || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-blue-50/40 to-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <div className="grid grid-cols-1 gap-6 px-6 py-8 lg:grid-cols-12 lg:px-8">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                <Building2 className="mr-1.5 h-3.5 w-3.5" />
                {t('recruiterCard')}
              </div>
              <h1 className="mt-3 text-3xl font-semibold leading-tight text-slate-900 sm:text-4xl">
                {t('title')} {recruiterName}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">{t('subtitle')}</p>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="space-y-2 text-sm text-slate-600">
                  <p className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{t('duration')}</span>
                    <span>{bookingSettings.booking_duration || 30} min</span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{t('minNotice')}</span>
                    <span>{bookingSettings.min_advance_booking || 24}h</span>
                  </p>
                  <p className="flex items-center justify-between">
                    <span className="font-medium text-slate-700">{t('maxAdvance')}</span>
                    <span>{bookingSettings.max_advance_booking || 30} j</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="space-y-6 lg:col-span-7"
          >
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{t('selectDate')}</label>
                  <div className="relative">
                    <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(event) => setSelectedDate(event.target.value)}
                      className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  {fieldErrors.selectedDate ? (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.selectedDate}</p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{t('selectTimezone')}</label>
                  <div className="relative">
                    <Globe2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      list="timezone-options"
                      value={candidateTimezone}
                      onChange={(event) => setCandidateTimezone(event.target.value)}
                      className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                    <datalist id="timezone-options">
                      {timezoneOptions.map((tz) => (
                        <option key={tz} value={tz} />
                      ))}
                    </datalist>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{t('timezoneHint')}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900">{t('selectTime')}</h2>
              <div className="mt-3">
                {!selectedDate ? (
                  <p className="text-sm text-slate-500">{t('chooseDateFirst')}</p>
                ) : slotsLoading ? (
                  <p className="text-sm text-slate-500">{t('loading')}</p>
                ) : availableSlots.length === 0 ? (
                  <p className="text-sm text-slate-500">{t('noSlots')}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.start}
                        type="button"
                        onClick={() => {
                          setSelectedSlot(slot);
                          setFieldErrors((prev) => ({ ...prev, selectedSlot: '' }));
                        }}
                        className={`inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium transition ${
                          selectedSlot?.start === slot.start
                            ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <Clock3 className="mr-1.5 h-3.5 w-3.5" />
                        {slot.start} - {slot.end}
                      </button>
                    ))}
                  </div>
                )}
                {fieldErrors.selectedSlot ? (
                  <p className="mt-2 text-xs text-red-600">{fieldErrors.selectedSlot}</p>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-base font-semibold text-slate-900">{t('meetingType')}</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {['online', 'physical'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    disabled={forcedMeetingType && forcedMeetingType !== type}
                    onClick={() => setMeetingType(type)}
                    className={`inline-flex items-center rounded-lg border px-4 py-2 text-sm font-medium transition ${
                      meetingType === type
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50'
                    } ${forcedMeetingType && forcedMeetingType !== type ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    {type === 'online' ? <Video className="mr-1.5 h-4 w-4" /> : <Building2 className="mr-1.5 h-4 w-4" />}
                    {t(type)}
                  </button>
                ))}
              </div>
              {forcedMeetingType ? (
                <p className="mt-2 text-xs text-slate-500">{t('meetingTypeLocked')}</p>
              ) : null}
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">{t('yourInfo')}</h2>
              <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{t('name')}</label>
                  <div className="relative">
                    <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={formData.candidateName}
                      onChange={(event) => {
                        setFormData((prev) => ({ ...prev, candidateName: event.target.value }));
                        setFieldErrors((prev) => ({ ...prev, candidateName: '' }));
                      }}
                      className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  {fieldErrors.candidateName ? <p className="mt-1 text-xs text-red-600">{fieldErrors.candidateName}</p> : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{t('email')}</label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="email"
                      value={formData.candidateEmail}
                      onChange={(event) => {
                        setFormData((prev) => ({ ...prev, candidateEmail: event.target.value }));
                        setFieldErrors((prev) => ({ ...prev, candidateEmail: '' }));
                      }}
                      className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                  {fieldErrors.candidateEmail ? <p className="mt-1 text-xs text-red-600">{fieldErrors.candidateEmail}</p> : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{t('phone')}</label>
                  <div className="relative">
                    <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={formData.candidatePhone}
                      onChange={(event) => setFormData((prev) => ({ ...prev, candidatePhone: event.target.value }))}
                      className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{t('subject')}</label>
                  <div className="relative">
                    <MessageSquare className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                    <textarea
                      rows={3}
                      value={formData.subject}
                      onChange={(event) => setFormData((prev) => ({ ...prev, subject: event.target.value }))}
                      className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex h-11 w-full items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
                >
                  {submitting ? t('booking') : t('book')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </form>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
